import * as _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';

import FuelTableRow from '../fuel-table-row/FuelTableRow';

// Group Options
// group: {
//   name: "shared", // String
//   pull: "clone", // true|false|["foo", "bar"]|'clone'|function
//   put: false, // true|false|["baz", "qux"]|function
//   revertClone: true, // boolean
// },

const FuelTableRows = ({
  rows,
  drop = false,
  drag = false,
  pull = false,
  onDrag = null,
  dropInPlace = false,
  rowRefs = null,
  dragAndDropText = 'Drag and Drop here',
}: {
  rows: any[];
  drop: boolean;
  drag: boolean;
  pull: boolean | string;
  onDrag: ((newItems: any[], lastDroppedElement: any) => void) | null;
  dropInPlace: boolean;
  rowRefs: any;
  dragAndDropText: string;
}) => {
  // set defaults
  if (dropInPlace !== true) dropInPlace = false;
  if (drop !== true) drop = false;
  if (drag !== true) drag = false;
  pull = pull === true ? 'clone' : false;

  let sortableDisabled = false;
  if (drop === false && drag === false && pull === false) {
    sortableDisabled = true;
  }

  const [state, setState] = useState<any[]>(rows);

  useEffect(() => {
    setState(rows);
  }, [rows]);

  const dragAndDropChanged = (newRows: any[], evt) => {
    const hasChange = !areEqual(newRows, rows);
    // console.log('FuelTableRows dragAndDropChanged', { onDrag, newRows, rows, hasChange, evt });

    // call the method only if something has changed
    if (newRows != null && hasChange) {
      const newData = [];
      let lastDroppedElement = null;
      const diff = _.difference(newRows, rows);
      if (diff.length > 0) {
        lastDroppedElement = diff[0].original;
      }
      for (let i = 0; i < newRows.length; i++) {
        newData.push(newRows[i].original);
      }
      if (dropInPlace) {
        // replace row item with dropped item from one before
        // find the item that is different from the rest and put it in the slot of the next item in the list
        // in other words we hid the row now we need to place it in the right slot
        for (let i = 0; i < newData.length; i++) {
          const element: any = newData[i];
          if (element.item === undefined && newData.length > i + 1 && newData[i + 1].item !== undefined) {
            const item = _.cloneDeep(newData[i]);
            newData[i + 1].item = item;
            newData.splice(i, 1);
            break;
          }
        }

        setTimeout(() => {
          const parent: HTMLElement = evt.el;
          removeHoveredClass(parent);
        }, 300);
      }
      if (onDrag) onDrag(newData, lastDroppedElement);
    }
  };

  const areEqual = (array1: any[], array2: any[]) => {
    if (array1 == null && array2 == null) return true;
    if ((array1 == null && array2 != null) || (array2 == null && array1 != null)) return false;

    if (array1.length === array2.length) {
      return array1.every((element, index) => {
        if (element === array2[index]) {
          return true;
        }
        return false;
      });
    }

    return false;
  };

  const onAdd = (evt, originalEvent) => {
    // console.log('onAdd', { evt, originalEvent });
    if (!dropInPlace) {
    } else {
      setTimeout(() => {
        const targetRow: HTMLElement = evt.el;
        if (targetRow) {
          removeHoveredClass(targetRow);
        }
      }, 300);
    }
  };

  // from the docs
  // https://github.com/SortableJS/Sortable#options
  //
  // onMove: function (/**Event*/evt, /**Event*/originalEvent) {
  // 	// Example: https://jsbin.com/nawahef/edit?js,output
  // 	evt.dragged; // dragged HTMLElement
  // 	evt.draggedRect; // DOMRect {left, top, right, bottom}
  // 	evt.related; // HTMLElement on which have guided
  // 	evt.relatedRect; // DOMRect
  // 	evt.willInsertAfter; // Boolean that is true if Sortable will insert drag element after target by default
  // 	originalEvent.clientY; // mouse position
  // 	// return false; — for cancel
  // 	// return -1; — insert before target
  // 	// return 1; — insert after target
  // 	// return true; — keep default insertion point based on the direction
  // 	// return void; — keep default insertion point based on the direction
  // },
  const onMove = (evt, originalEvent) => {
    const isTargetDropInPlace = evt.to.classList.contains('drop-in-place');
    // console.log('onMove', { evt, originalEvent, isTargetDropInPlace });

    if (!isTargetDropInPlace) {
      return true;
    }

    const targetRow: HTMLElement = evt.related;
    if (targetRow) {
      const parent = targetRow.parentElement;
      removeHoveredClass(parent);
      targetRow.classList.add('fuel-table-row--hovered');
    }
    return -1; // insert before target (index - 1)
  };

  const onStart = (evt) => {
    // Disable scrollbar for firefox
    if (navigator.userAgent.includes('Firefox')) {
      const ele = document.getElementById('fuel-asset-search-table');
      if (ele) {
        ele.classList.add('fuel-asset-search-table');
      }
    }
  };
  const onEnd = (evt) => {
    // console.log('onEnd', { evt });
    // remove Disable scrollbar for firefox
    if (navigator.userAgent.includes('Firefox')) {
      const ele = document.getElementById('fuel-asset-search-table');
      if (ele) {
        ele.classList.remove('fuel-asset-search-table');
      }
    }
  };

  const removeHoveredClass = (parent) => {
    if (parent) {
      const children = parent?.children;
      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        element.classList.remove('fuel-table-row--hovered');
      }
    }
  };

  const logEvent = (eventName, evt) => {
    console.info(eventName, { evt });
    return true;
  };

  return (
    <ReactSortable
      className={dropInPlace ? 'fuel-table-rows MuiTableBody-root drop-in-place' : 'fuel-table-rows MuiTableBody-root'}
      disabled={sortableDisabled}
      ghostClass={dropInPlace ? 'sortable-ghost-hidden' : 'sortable-ghost'}
      sort={drag}
      group={{ name: 'shared', pull, put: drop, revertClone: true }}
      tag="tbody"
      list={state}
      setList={dragAndDropChanged}
      handle=".fuel-table-row"
      // setData={(evt) => logEvent('setData', evt)}
      // onChoose={(evt) => logEvent('onChoose', evt)}
      // onUnchoose={(evt) => logEvent('onUnchoose', evt)}
      onStart={(evt) => onStart(evt)}
      onEnd={(evt) => onEnd(evt)}
      // onAdd={onAdd}
      // onUpdate={(evt) => logEvent('onUpdate', evt)}
      // onRemove={(evt) => logEvent('onRemove', evt)}
      // onFilter={(evt) => logEvent('onFilter', evt)}
      onMove={onMove}
    // onMove={(evt) => logEvent('onMove', evt)}
    // onChange={(evt) => logEvent('onChange', evt)}
    // onClone={(evt) => logEvent('onClone', evt)}
    >
      {state && state.length > 0 ? (
        state.map((row, rowIndex) => (
          <React.Fragment key={row.id}>
            <FuelTableRow row={row} rowRefs={rowRefs} />
            {!dropInPlace && drop && rowIndex == state.length - 1 && (
              <tr>
                <td className="drop-zone" colSpan="100%">
                  {dragAndDropText}
                </td>
              </tr>
            )}
          </React.Fragment>
        ))
      ) : drop ? (
        <tr>
          <td className="drop-zone" colSpan="100%">
            {dragAndDropText}
          </td>
        </tr>
      ) : (
        <tr />
      )}
    </ReactSortable>
  );
};

export default FuelTableRows;
